import { DashboardLayout } from "@/components/DashboardLayout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RestaurantPlanCard, ContactSalesCard, EventPlanCard } from "@/components/PlanCards";
import { useBilling } from "@/lib/useBilling";

function CurrentPlanBanner({ sub }) {
  if (!sub?.subscription) return null;
  const s = sub.subscription;
  return (
    <Card
      className="p-5 border border-border mb-8 bg-brand-navy text-white flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between"
      data-testid="current-plan-banner"
    >
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange">Current plan</div>
        <div className="font-heading font-bold text-lg mt-1">{s.plan}</div>
        <div className="text-xs text-white/60 mt-1">
          {s.is_event_pack
            ? "Event pack"
            : s.unlimited
              ? `${sub.outlet_count} outlets · unlimited`
              : `${sub.outlet_count}/${s.outlet_limit} outlets used`}
          {s.ends_at ? ` · expires ${new Date(s.ends_at).toLocaleDateString()}` : ""}
        </div>
      </div>
      {s.grandfathered && <Badge className="bg-veg hover:bg-veg">Free legacy</Badge>}
    </Card>
  );
}

export default function Billing() {
  const { plansData, sub, config, busy, buyPlan } = useBilling();

  if (!plansData) {
    return (
      <DashboardLayout role="owner">
        <div className="text-muted-foreground">Loading plans…</div>
      </DashboardLayout>
    );
  }

  const restaurantPlans = plansData.plans.filter((p) => !p.is_event);
  const eventPlan = plansData.plans.find((p) => p.is_event);
  const currentPlan = sub?.subscription?.plan;
  const sym = "₹";

  return (
    <DashboardLayout role="owner">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange mb-2">Billing</div>
        <h1 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight">Choose your plan</h1>
        <p className="text-foreground/60 mt-2">Annual restaurant plans or a one-off event pack — pay once, get a branded QR.</p>
      </div>

      <CurrentPlanBanner sub={sub} />

      <h2 className="font-heading font-semibold text-xl mb-4">Restaurant plans (annual)</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
        {restaurantPlans.map((p) => (
          <RestaurantPlanCard
            key={p.id}
            plan={p}
            isCurrent={currentPlan === p.id}
            busy={busy === p.id}
            disabled={!config.enabled}
            onBuy={buyPlan}
            sym={sym}
          />
        ))}
        <ContactSalesCard email={plansData.contact_email} />
      </div>

      {eventPlan && (
        <>
          <h2 className="font-heading font-semibold text-xl mb-4">One-off event</h2>
          <EventPlanCard
            plan={eventPlan}
            busy={busy === eventPlan.id}
            disabled={!config.enabled}
            onBuy={buyPlan}
            sym={sym}
          />
        </>
      )}

      {!config.enabled && (
        <p className="text-xs text-muted-foreground mt-6">
          Razorpay not configured — checkout disabled. Add keys in <code>.env</code> to activate.
        </p>
      )}
    </DashboardLayout>
  );
}
